import React, { useEffect, useState } from 'react';
import truncate from 'lodash/truncate';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { richText } from '../../../util/richText';
import { ensureUser, ensureCurrentUser } from '../../../util/data';
import { propTypes } from '../../../util/types';

import { AvatarLarge, NamedLink, InlineTextButton, IconReviewStar, IconPhone, IconGlobe } from '../../../components';
import ReportListingModal from '../ReportListingModal/ReportListingModal';

import css from './UserCard.module.css';

// Approximated collapsed size so that there are ~three lines of text
// in the desktop layout in the author section of the ListingPage.
const BIO_COLLAPSED_LENGTH = 170;
const MIN_LENGTH_FOR_LONG_WORDS = 20;

const truncated = s => {
  return truncate(s, {
    length: BIO_COLLAPSED_LENGTH,

    // Allow truncated text end only in specific characters. This will
    // make the truncated text shorter than the length if the original
    // text has to be shortened and the substring ends in a separator.
    //
    // This ensures that the final text doesn't get cut in the middle
    // of a word.
    separator: /\s|,|\.|:|;/,
    omission: '…',
  });
};

const ExpandableBio = props => {
  const [expand, setExpand] = useState(false);
  const { className, bio } = props;
  const bioWithLinks = richText(bio, {
    linkify: true,
    longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
    longWordClass: css.longWord,
  });
  const truncatedBio = richText(truncated(bio), {
    linkify: true,
    longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
    longWordClass: css.longWord,
    breakChars: '/',
  });

  const handleShowMoreClick = () => {
    setExpand(true);
  };
  const showMore = (
    <InlineTextButton rootClassName={css.showMore} onClick={handleShowMoreClick}>
      <FormattedMessage id="UserCard.showFullBioLink" />
    </InlineTextButton>
  );
  return (
    <p className={className}>
      {expand ? bioWithLinks : truncatedBio}
      {bio.length >= BIO_COLLAPSED_LENGTH && !expand ? showMore : null}
    </p>
  );
};

const toValidDate = value => {
  if (!value) {
    return null;
  }
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const toFiniteNumber = value => {
  const num = typeof value === 'string' ? Number(value) : value;
  return typeof num === 'number' && Number.isFinite(num) ? num : null;
};

/**
 * A single trust signal. Renders nothing without a value, so that the row
 * collapses instead of showing an empty slot.
 */
const TrustStat = props => {
  const { label, children } = props;
  return children == null ? null : (
    <div className={css.trustStat}>
      <span className={css.trustLabel}>{label}</span>
      <span className={css.trustValue}>{children}</span>
    </div>
  );
};

/**
 * Trust signals derived from the author entity. Every field is optional: the
 * Marketplace API only guarantees displayName/abbreviatedName on a public
 * profile, and operators may or may not write rating/response data into the
 * user's public data or metadata. Nothing here is required to exist.
 */
const SellerTrustStats = props => {
  const { user, intl } = props;

  const profile = user?.attributes?.profile || {};
  const profilePublicData = profile.publicData || {};
  const profileMetadata = profile.metadata || {};

  const joinDate = toValidDate(user?.attributes?.createdAt || profilePublicData.memberSince);

  // Operators commonly denormalise aggregate review data onto the profile.
  const rating = toFiniteNumber(profileMetadata.rating ?? profilePublicData.rating);
  const reviewCount = toFiniteNumber(profileMetadata.reviewCount ?? profilePublicData.reviewCount);

  const responseRate = toFiniteNumber(
    profileMetadata.responseRate ?? profilePublicData.responseRate
  );
  const responseTime = profileMetadata.responseTime ?? profilePublicData.responseTime;
  const hasResponseTime = typeof responseTime === 'string' && responseTime.length > 0;

  const hasAnyStat =
    joinDate != null || rating != null || reviewCount != null || responseRate != null;

  if (!hasAnyStat && !hasResponseTime) {
    return null;
  }

  return (
    <div className={css.trustStats}>
      <TrustStat label={<FormattedMessage id="UserCard.joinedLabel" />}>
        {joinDate ? intl.formatDate(joinDate, { month: 'long', year: 'numeric' }) : null}
      </TrustStat>

      <TrustStat label={<FormattedMessage id="UserCard.ratingLabel" />}>
        {rating != null ? (
          <span className={css.ratingValue}>
            <IconReviewStar isFilled className={css.ratingStar} />
            {intl.formatNumber(rating, { maximumFractionDigits: 1 })}
          </span>
        ) : null}
      </TrustStat>

      <TrustStat label={<FormattedMessage id="UserCard.reviewsLabel" />}>
        {reviewCount != null ? intl.formatNumber(reviewCount) : null}
      </TrustStat>

      <TrustStat label={<FormattedMessage id="UserCard.responseRateLabel" />}>
        {responseRate != null ? intl.formatNumber(responseRate / 100, { style: 'percent' }) : null}
      </TrustStat>

      <TrustStat label={<FormattedMessage id="UserCard.responseTimeLabel" />}>
        {hasResponseTime ? responseTime : null}
      </TrustStat>
    </div>
  );
};

/**
 * The UserCard component.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {propTypes.user | propTypes.currentUser} props.user - The user
 * @param {propTypes.currentUser} props.currentUser - The current user
 * @param {function} props.onContactUser - The on contact user function
 * @param {boolean} [props.showContact] - Whether to show the contact user button
 * @returns {JSX.Element} user card component
 */
const UserCard = props => {
  const [mounted, setMounted] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const intl = useIntl();
  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    rootClassName,
    className,
    user,
    currentUser,
    listing,
    onManageDisableScrolling,
    onContactUser,
    showContact = true,
    contactLinkId = 'contactUserLink',
  } = props;

  const userIsCurrentUser = user && user.type === 'currentUser';
  const ensuredUser = userIsCurrentUser ? ensureCurrentUser(user) : ensureUser(user);

  const ensuredCurrentUser = ensureCurrentUser(currentUser);
  const isCurrentUser =
    ensuredUser.id && ensuredCurrentUser.id && ensuredUser.id.uuid === ensuredCurrentUser.id.uuid;
  const { displayName, bio, publicData } = ensuredUser.attributes.profile || {};
  const isBusiness = publicData?.userType === 'business';
  const businessName = publicData?.businessName;
  const renderedName = isBusiness && businessName ? businessName : displayName;
  const phone = publicData?.phone;
  const website = publicData?.website;

  const handleContactUserClick = () => {
    onContactUser(user);
  };

  const hasBio = !!bio;
  const classes = classNames(rootClassName || css.root, className);
  const linkClasses = classNames(css.links, {
    [css.withBioMissingAbove]: !hasBio,
  });

  const separator =
    (mounted && isCurrentUser) || !showContact ? null : (
      <span className={css.linkSeparator}>•</span>
    );

  const contact = showContact ? (
    <InlineTextButton
      id={contactLinkId}
      rootClassName={css.contact}
      onClick={handleContactUserClick}
      enforcePagePreloadFor="SignupPage"
    >
      <FormattedMessage id="UserCard.contactUser" />
    </InlineTextButton>
  ) : null;

  const reportSeparator = (mounted && !isCurrentUser && ensuredUser.id) ? (
    <span className={css.linkSeparator}>•</span>
  ) : null;
  
  const reportUser = (mounted && !isCurrentUser && ensuredUser.id) ? (
    <InlineTextButton
      rootClassName={css.contact}
      onClick={() => setIsReportModalOpen(true)}
    >
      <FormattedMessage id="UserCard.reportUser" defaultMessage="Report listing" />
    </InlineTextButton>
  ) : null;

  const editProfileMobile = (
    <span className={css.editProfileMobile}>
      <span className={css.linkSeparator}>•</span>
      <NamedLink name="ProfileSettingsPage">
        <FormattedMessage id="ListingPage.editProfileLink" />
      </NamedLink>
    </span>
  );

  const editProfileDesktop =
    mounted && isCurrentUser ? (
      <NamedLink className={css.editProfileDesktop} name="ProfileSettingsPage">
        <FormattedMessage id="ListingPage.editProfileLink" />
      </NamedLink>
    ) : null;

  const links = ensuredUser.id ? (
    <p className={linkClasses}>
      <NamedLink className={css.link} name="ProfilePage" params={{ id: ensuredUser.id.uuid }}>
        <FormattedMessage id="UserCard.viewProfileLink" />
      </NamedLink>
      {separator}
      {mounted && isCurrentUser ? editProfileMobile : contact}
      {reportSeparator}
      {reportUser}
    </p>
  ) : null;

  return (
    <div className={classes}>
      <div className={css.content}>
        <AvatarLarge className={css.avatar} user={user} />
        <div className={css.info}>
          <div className={css.headingRow}>
            <span className={css.heading}>
              <FormattedMessage id="UserCard.heading" values={{ name: renderedName }} />
              {isBusiness ? <span className={css.merchantBadge}>Merchant</span> : null}
            </span>
            {editProfileDesktop}
          </div>
          {isBusiness && (phone || website) ? (
            <div className={css.merchantDirectContact}>
              {phone ? (
                <a href={`tel:${phone}`} className={css.merchantDirectLink}>
                  <IconPhone className={css.merchantLinkIcon} /> {phone}
                </a>
              ) : null}
              {website ? (
                <a
                  href={website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={css.merchantDirectLink}
                >
                  <IconGlobe className={css.merchantLinkIcon} /> Visit Website
                </a>
              ) : null}
            </div>
          ) : null}
          <SellerTrustStats user={ensuredUser} intl={intl} />
          {hasBio ? <ExpandableBio className={css.desktopBio} bio={bio} /> : null}
          {links}
        </div>
      </div>
      {hasBio ? <ExpandableBio className={css.mobileBio} bio={bio} /> : null}
      {isReportModalOpen ? (
        <ReportListingModal
          id="UserCard.ReportListingModal"
          isOpen={isReportModalOpen}
          onClose={() => setIsReportModalOpen(false)}
          onManageDisableScrolling={onManageDisableScrolling || (() => {})}
          listing={listing}
          currentUser={currentUser}
        />
      ) : null}
    </div>
  );
};

export default UserCard;
